import dayjs from "dayjs";
import * as Yup from "yup";
import { emailRegex } from "./helpers";

/* Login */
export const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required("errors.email_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error")
    .matches(emailRegex, "errors.invalid_email"),
  password: Yup.string().required("errors.password_error"),
});

/* Register */
export const registerEmailValidation = Yup.object().shape({
  email: Yup.string()
    .matches(emailRegex, "errors.invalid_email")
    .required("errors.email_error"),
});

export const registerPhoneValidation = Yup.object().shape({
  phone: Yup.string()
    .required("errors.phone_error")
    .min(6, "errors.phone_length_error")
    .max(14, "errors.phone_length_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
});

export const registerUserInfoValidation = Yup.object().shape({
  first_name: Yup.string()
    .required("errors.first_name_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error")
    .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
    .max(15, "errors.name_max_length"),
  last_name: Yup.string()
    .required("errors.last_name_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error")
    .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
    .max(15, "errors.name_max_length"),
  gender: Yup.string().required("errors.gender_error"),
  nationality: Yup.string().required("errors.nationality_errr"),
  day: Yup.string().required("errors.day_error"),
  month: Yup.string().required("errors.month_error"),
  year: Yup.string()
    .required("errors.year_error")
    .test("is-old-enough", "errors.age_16_error", function (year) {
      const { day, month } = this.parent;
      if (!day || !month || !year) return false;
      const birthdate = dayjs(`${year}-${month}-${day}`);
      return dayjs().diff(birthdate, "year") >= 16;
    }),
});

export const registerAddressInformation = Yup.object({
  city: Yup.string()
    .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.city_matches_error")
    .required("errors.city_error"),
  state: Yup.string()
    .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.state_matches_error")
    .required("errors.state_error"),
  postcode: Yup.string()
    .matches(/^\d{4,10}$/, "errors.postal_must_error")
    .required("errors.postal_error"),
  street: Yup.string()
    .matches(/^[\w\u0600-\u06FFÀ-ÿ'.\- ]{3,}$/, "errors.street_matches_error")
    .required("errors.street_error"),
});

export const agentCreateUser = () =>
  Yup.object({
    email: Yup.string()
      .matches(emailRegex, "errors.invalid_email")
      .required("errors.email_error"),
    phone: Yup.string()
      .required("errors.phone_error")
      .min(6, "errors.phone_length_error")
      .max(14, "errors.phone_length_error")
      .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
    firstName: Yup.string()
      .required("errors.first_name_error")
      .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error")
      .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
      .max(15, "errors.name_max_length"),
    lastName: Yup.string()
      .required("errors.last_name_error")
      .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error")
      .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
      .max(15, "errors.name_max_length"),
    gender: Yup.string().required("errors.gender_error"),
    nationality: Yup.string().required("errors.nationality_errr"),
    day: Yup.string().required("errors.day_error"),
    month: Yup.string().required("errors.month_error"),
    year: Yup.string()
      .required("errors.year_error")
      .test("is-old-enough", "errors.age_16_error", function (year) {
        const { day, month } = this.parent;
        if (!day || !month || !year) return false;
        const birthdate = dayjs(`${year}-${month}-${day}`);
        return dayjs().diff(birthdate, "year") >= 16;
      }),
    city: Yup.string()
      .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.city_matches_error")
      .required("errors.city_error"),
    state: Yup.string()
      .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.state_matches_error")
      .required("errors.state_error"),
      postcode: Yup.string()
      .matches(/^\d{4,10}$/, "errors.postal_must_error")  // using '\d' for digits
      .required("errors.postal_error"),
    
    street: Yup.string()
      .matches(/^[\w\u0600-\u06FFÀ-ÿ'.\- ]{3,}$/, "errors.street_matches_error")
      .required("errors.street_error"),
  });

export const registerPasswordValidation = (t) =>
  Yup.object().shape({
    password: Yup.string()
      .required(t("errors.password_error"))
      .min(8, t("errors.password_min_error"))
      .max(20, t("errors.password_max_error"))
      .matches(/[A-Z]/, t("errors.password_uppercase_error"))
      .matches(/[a-z]/, t("errors.password_lowercase_error"))
      .matches(/[@$!%*?&#^_-]/, t("errors.password_symbol_error")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "errors.confirm_password_error2")
      .required("errors.confirm_password_error"),
  });

export const forgetPasswordEmailValidation = (t) =>
  Yup.object().shape({
    email: Yup.string()
      .matches(emailRegex, "errors.invalid_email")
      .required("errors.email_error"),
  });

export const forgetPasswordResetPasswordValidation = (t) =>
  Yup.object().shape({
    newPassword: Yup.string()
      .required(t("errors.password_error"))
      .min(8, t("errors.password_min_error"))
      .max(20, t("errors.password_max_error"))
      .matches(/[A-Z]/, t("errors.password_uppercase_error"))
      .matches(/[a-z]/, t("errors.password_lowercase_error"))
      .matches(/[@$!%*?&#^_-]/, t("errors.password_symbol_error")),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "errors.confirm_password_error2")
      .required("errors.confirm_password_error"),
  });

/* Update profile */
export const updateProfileValidation = Yup.object().shape({
  email: Yup.string()
    .matches(emailRegex, "errors.invalid_email")
    .required("errors.email_error"),
  mobile_number: Yup.string()
    .required("errors.phone_error")
    .min(6, "errors.phone_length_error")
    .max(14, "errors.phone_length_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  first_name: Yup.string()
    .required("errors.first_name_error")
    .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  last_name: Yup.string()
    .required("errors.last_name_error")
    .matches(/^[\p{L}\s]+$/u, "errors.no_special_characters_allowed")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  day: Yup.string().required("errors.day_error"),
  month: Yup.string().required("errors.month_error"),
  year: Yup.string()
    .required("errors.year_error")
    .test("is-old-enough", "errors.age_16_error", function (year) {
      const { day, month } = this.parent;
      if (!day || !month || !year) return false;
      const birthdate = dayjs(`${year}-${month}-${day}`);
      return dayjs().diff(birthdate, "year") >= 16;
    }),
  nationality: Yup.string().required("errors.nationality_errr"),
  city: Yup.string()
    .required("errors.city_error")
    .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.city_matches_error"),
  state: Yup.string()
    .required("errors.state_error")
    .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.state_matches_error"),
  street: Yup.string()
    .required("errors.street_error")
    .matches(/^[\w\u0600-\u06FFÀ-ÿ'.\- ]{3,}$/, "errors.street_matches_error"),
});

/* Airport trip */
export const AirportRideDetailsValidation = (agent) =>
  Yup.object().shape({
    agentUser: Yup.mixed().test(
      "is-valid",
      "errors.user_required",
      function (value) {
        if (!agent) {
          return true;
        }
        return Boolean(value); // Ensures the value is truthy
      }
    ),
    rideType: Yup.string().required("errors.ride_type_is_required"),
    arrivalCity: Yup.string().required("errors.arrival_city_is_required"),
    airportName: Yup.string().required("errors.airport_name_is_required"),
    terminalNumber: Yup.string().required("errors.terminal_number_is_required"),
    location: Yup.string().required("hero.errors.map_required"),
    destination: Yup.string().required("hero.errors.map_required"),
  });

// Join Agent validation schema

export const joinAgentValidationSchema = Yup.object().shape({
  company_name: Yup.string()
    .required("errors.company_name_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  email: Yup.string()
    .matches(emailRegex, "errors.invalid_email")
    .required("errors.email_error"),
  mobile: Yup.string()
    .required("errors.phone_error")
    .min(6, "errors.phone_length_error")
    .max(14, "errors.phone_length_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  company_sector: Yup.string()
    .required("errors.company_sector_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  city: Yup.string()
    .required("errors.city_error")
    .matches(/^[A-Za-z\u0600-\u06FFÀ-ÿ'.\- ]{2,}$/, "errors.city_matches_error"),
  country: Yup.string()
    .required("errors.country_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
  description: Yup.string()
    .required("errors.description_error")
    .matches(/^\S+(?: \S+)*$/, "errors.white_spaces_error"),
});

export const AirportVehicleDetailsValidation = (t, bookingTime) =>
  Yup.object().shape({
    seatNumber: Yup.mixed().test(
      "is-valid",
      "errors.seat_number_error",
      function (value) {
        const { sharedRide } = this.parent;
        if (!sharedRide) {
          return true;
        }
        return Boolean(value); // Ensures the value is truthy
      }
    ),
    arrivalDate: Yup.mixed()
      .required("errors.arrival_date_error")
      .test("is-not-in-past", "Arrival Date cannot be in the past", (value) => {
        return value.isSame(dayjs(), "day") || value.isAfter(dayjs(), "day");
      }),

    arrivalTime: Yup.mixed()
      .required(t("errors.arrival_time_error"))
      .test(
        "is-valid-time",
        `${t("errors.hour_2_error")} ${bookingTime} ${t("hero.hours")}`,
        function (value) {
          const { arrivalDate } = this.parent;
          if (
            !arrivalDate ||
            arrivalDate.isAfter(dayjs().add(bookingTime, "hours"), "day")
          ) {
            return true;
          }
          return (
            value.isSame(dayjs().add(bookingTime, "hours"), "minutes") ||
            value.isAfter(dayjs().add(bookingTime, "hours"), "minutes")
          );
        }
      ),
  });

export const RideByHourRideDetailsValidation = (t, bookingTime, agent) =>
  Yup.object().shape({
    agentUser: Yup.mixed().test(
      "is-valid",
      "errors.user_required",
      function (value) {
        if (!agent) {
          return true;
        }
        return Boolean(value); // Ensures the value is truthy
      }
    ),
    hours: Yup.string().required("errors.ride_hours_is_required"),
    arrivalCity: Yup.string().required("errors.arrival_city_is_required"),
    arrivalDate: Yup.mixed()
      .required("errors.arrival_date_error")
      .test("is-not-in-past", "Arrival Date cannot be in the past", (value) => {
        return value.isSame(dayjs(), "day") || value.isAfter(dayjs(), "day");
      }),
    arrivalTime: Yup.mixed()
      .required(t("errors.arrival_time_error"))
      .test(
        "is-valid-time",
        `${t("errors.hour_2_error")} ${bookingTime} ${t("hero.hours")}`,
        function (value) {
          const { arrivalDate } = this.parent;
          if (
            !arrivalDate ||
            arrivalDate.isAfter(dayjs().add(bookingTime, "hours"), "day")
          ) {
            return true;
          }
          return (
            value.isSame(dayjs().add(bookingTime, "hours"), "minutes") ||
            value.isAfter(dayjs().add(bookingTime, "hours"), "minutes")
          );
        }
      ),
    location: Yup.string().required("hero.errors.map_required"),
  });

/* City trip */
export const CityTripRideDetailsValidation = (t, bookingTime, agent) =>
  Yup.object().shape({
    agentUser: Yup.mixed().test(
      "is-valid",
      "errors.user_required",
      function (value) {
        if (!agent) {
          return true;
        }
        return Boolean(value); // Ensures the value is truthy
      }
    ),
    arrivalCity: Yup.string().required("errors.arrival_city_is_required"),
    location: Yup.string().required("hero.errors.map_required"),
    destination: Yup.string().required("hero.errors.map_required"),
    arrivalDate: Yup.mixed()
      .required("errors.arrival_date_error")
      .test("is-not-in-past", "Arrival Date cannot be in the past", (value) => {
        return value.isSame(dayjs(), "day") || value.isAfter(dayjs(), "day");
      }),

    arrivalTime: Yup.mixed()
      .required(t("errors.arrival_time_error"))
      .test(
        "is-valid-time",
        `${t("errors.hour_2_error")} ${bookingTime} ${t("hero.hours")}`,
        function (value) {
          const { arrivalDate } = this.parent;
          if (
            !arrivalDate ||
            arrivalDate.isAfter(dayjs().add(bookingTime, "hours"), "day")
          ) {
            return true;
          }
          return (
            value.isSame(dayjs().add(bookingTime, "hours"), "minutes") ||
            value.isAfter(dayjs().add(bookingTime, "hours"), "minutes")
          );
        }
      ),
  });

export const CityTripVehicleDetailsValidation = Yup.object().shape({
  seatNumber: Yup.mixed().test(
    "is-valid",
    "errors.seat_number_error",
    function (value) {
      const { sharedRide } = this.parent;
      if (!sharedRide) {
        return true;
      }
      return sharedRide && value;
    }
  ),
});
